import { useEventListener, useLatest } from 'ahooks';
import classNames from 'classnames';
import { cloneDeep, isEqual, maxBy, noop, range, sum } from 'lodash';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Message } from '@meitu/saas-ui';
import IconFont from '@meitu/xdesign-icon-font';
import { useFramework, useScale } from '@/framework';
import { useLocaleStore } from '@/i18n/index';
import { getRowOrColActions, InsertDirection } from '@/layers/table-layer/actions/row-col-actions';
import {
    LINE_BOLD,
    LINE_HOVER_BOLD,
    MAX_COL_COUNT,
    MAX_ROW_COUNT,
    MIN_BASE_CELL_HEIGHT,
    MIN_BASE_CELL_WIDTH,
    SIDE_BAR_BOLD,
} from '@/layers/table-layer/default-configs';
import { getFixedTableNumber } from '@/layers/table-layer/utils/fix-number';
import { getCellStyle, getUpdatedTableStyle, hoverAreaClassName } from '@/layers/table-layer/utils/table';
import { getTdElementId } from '@/layers/table-layer/view/cell-component';
import { arePropsEqual, COL_ELEMENT_IS_BLOCK, registerStyleSheet } from '@/utils/common-kit';
import { useResizeCursor } from '@/utils/cursor-kit';
import { Cell, ITableLayer, ITableLayerProps, RowOrColSelectedRectInfo } from '@/version-types';
import { CellsSelectedInfo, useTableEditState } from '../state';
import addOrUpdateLayer from '../utils/add-or-update-layer';
import './table-controller.less';

type TableInfo = Pick<ITableLayer, 'id' | 'width' | 'height' | 'top' | 'left' | 'rotate'>;

interface TableControllerProps {
    tableInfo: TableInfo;
    tableLayerData: ITableLayerProps;
}

const hiddenLine = () => {
    const removeStyle = registerStyleSheet(`
        .tableController-lines {
            display: none;
        }
    `);
    return removeStyle;
};

const TableController = memo<TableControllerProps>(
    ({ tableInfo, tableLayerData }) => {
        // console.time('===render TableController');
        const {
            baseCells: { horizontal: hSizes, vertical: vSizes },
            cells: cellsProp,
        } = tableLayerData;

        const framework = useFramework();
        const { setState } = useTableEditState();
        const rowOrColSelectedRectInfo = useTableEditState()((s) => s.rowOrColSelectedRectInfo);

        const firstCellStyle = getCellStyle(cellsProp[0][0]!, tableLayerData);
        const tableFakeBorderTop = (firstCellStyle.border?.top?.width ?? 0) / 2;
        const tableFakeBorderLeft = (firstCellStyle.border?.left?.width ?? 0) / 2;
        const tableFakeBorderRight = tableInfo.width - tableFakeBorderLeft - sum(hSizes);
        const tableFakeBorderBottom = tableInfo.height - tableFakeBorderTop - sum(vSizes);
        const tableFakeBorder = {
            top: tableFakeBorderTop,
            left: tableFakeBorderLeft,
            right: tableFakeBorderRight,
            bottom: tableFakeBorderBottom,
        };

        const { selectedRectNode, onSelectedRowOrCol } = useRowOrColSelectedRect({
            tableInfo,
            tableLayerData,
            tableFakeBorder,
        });
        const lines = useMemo(
            () => getLines(tableLayerData, tableFakeBorder),
            [tableLayerData, JSON.stringify(tableFakeBorder)],
        );
        const { movingLineId, onLineMouseDown: _onLineMouseDown } = useDragLine(tableInfo.id, lines);
        const _onLineMouseDownRef = useLatest(_onLineMouseDown);

        const cursorClassNames = useResizeCursor(tableInfo.rotate);

        useEffect(() => {
            if (!movingLineId) return;
            framework.operate.disableMove();
            const cursorClassName = movingLineId.direction === 'vertical' ? cursorClassNames.LC : cursorClassNames.TC;
            document.body.classList.add(cursorClassName);
            return () => {
                framework.operate.enableMove();
                document.body.classList.remove(cursorClassName);
            };
        }, [!!movingLineId]);

        const onLineMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement, MouseEvent>, line: Line) => {
            // 阻止tableLayer的移动操作
            e.stopPropagation();
            setState({
                cellsIsSelecting: false,
                lineIsDragging: true,
            });
            _onLineMouseDownRef.current?.(e, line.lineId);
        }, []);

        const sideBarBoldCss = { '--sideBarBold': `${SIDE_BAR_BOLD}px` } as any;
        const linesCss = { '--lineHoverBold': `${LINE_HOVER_BOLD}px`, '--lineBold': `${LINE_BOLD}px` } as any;
        const res = (
            <div className="tableController">
                {selectedRectNode}
                <div className="tableController-sideBar" style={sideBarBoldCss}>
                    <div className="tableController-cornerBtn" onMouseDown={(e) => e.stopPropagation()} />
                    {(['horizontalSide', 'verticalSide'] as const).map((sideMode) => {
                        const sizes = sideMode === 'horizontalSide' ? hSizes : vSizes;
                        return (
                            <div key={sideMode} className={`tableController-${sideMode}`}>
                                <div className="tableController-sideBtns">
                                    {sizes.map((size, index) => {
                                        const isLast = sizes.length - 1 === index;
                                        const cellSize = (() => {
                                            let cellSize = size;
                                            if (index == 0)
                                                cellSize +=
                                                    sideMode === 'horizontalSide'
                                                        ? tableFakeBorderLeft
                                                        : tableFakeBorderTop;
                                            if (isLast)
                                                cellSize +=
                                                    sideMode === 'horizontalSide'
                                                        ? tableFakeBorderRight
                                                        : tableFakeBorderBottom;
                                            return cellSize;
                                        })();
                                        const leftOrTop =
                                            index === 0
                                                ? 0
                                                : (sideMode === 'horizontalSide'
                                                      ? tableFakeBorderLeft
                                                      : tableFakeBorderTop) + sum(sizes.slice(0, index));
                                        const isSelected =
                                            rowOrColSelectedRectInfo?.direction ===
                                                (sideMode === 'horizontalSide' ? 'vertical' : 'horizontal') &&
                                            rowOrColSelectedRectInfo?.index === index;
                                        return (
                                            <div
                                                key={`${sideMode}-${index}`}
                                                className="tableController-sideBtn"
                                                data-is-selected={isSelected}
                                                style={
                                                    sideMode === 'horizontalSide'
                                                        ? { width: cellSize, left: leftOrTop }
                                                        : { height: cellSize, top: leftOrTop }
                                                }
                                            ></div>
                                        );
                                    })}
                                </div>
                                <div className="sideHoverAreas">
                                    {range(sizes.length + 1).map((index) => (
                                        <SideBarHoverArea
                                            key={`${sideMode}-${index}`}
                                            tableInfo={tableInfo}
                                            tableLayerData={tableLayerData}
                                            tableFakeBorder={tableFakeBorder}
                                            direction={sideMode === 'horizontalSide' ? 'horizontal' : 'vertical'}
                                            rowOrColIndex={index}
                                            onSelectedRowOrCol={(index) =>
                                                onSelectedRowOrCol({
                                                    direction:
                                                        sideMode === 'horizontalSide' ? 'vertical' : 'horizontal',
                                                    index,
                                                })
                                            }
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="tableController-lines" style={linesCss}>
                    {movingLineId ? (
                        <>
                            <LineComponent
                                key={`${movingLineId.direction}-${movingLineId.index}`}
                                line={
                                    lines.find(
                                        (l) =>
                                            l.lineId.direction === movingLineId.direction &&
                                            l.lineId.index === movingLineId.index,
                                    )!
                                }
                                tableRotate={tableInfo.rotate}
                                onMouseDown={onLineMouseDown}
                            />
                            <style
                                dangerouslySetInnerHTML={{
                                    __html: `
                                        .tableController-line[data-line-id="${movingLineId.direction}-${movingLineId.index}"]
                                        > .tableController-miniLine::before {
                                            background-color: var(--content-system-link, #38f);
                                        }
                                        .tableController-line:hover
                                        > .tableController-miniLine::before{
                                            background-color: initial !important;
                                        }
                                        .tableController-miniLine {
                                            cursor: inherit !important;
                                        }
                                    `,
                                }}
                            />
                        </>
                    ) : (
                        lines.map((line) => (
                            <LineComponent
                                key={`${line.lineId.direction}-${line.lineId.index}`}
                                line={line}
                                tableRotate={tableInfo.rotate}
                                onMouseDown={onLineMouseDown}
                            />
                        ))
                    )}
                </div>
            </div>
        );

        // console.timeEnd('===render TableController');
        return res;
    },
    (preProps, nextProps) => {
        return isEqual(preProps.tableInfo, nextProps.tableInfo) && arePropsEqual(preProps, nextProps, ['tableInfo']);
    },
);

TableController.displayName = 'TableController';

export default TableController;

const getLayerResizeOperateAreaRect = (type: 'rc' | 'bc') => {
    const el = document.querySelector(`[data-operate-type="resize"][data-direction="${type}"]`);
    return el?.getBoundingClientRect() ?? null;
};

const useDragLine = (layerId: string, lines: Line[]) => {
    const { handleDragLineStart, handleDragLine, handleDragLineEnd } = useHandleDragLine(layerId);
    const { setState } = useTableEditState();

    const [movingLineId, setMovingLineId] = useState<LineId | null>(null);
    const movingLineIdRef = useLatest(movingLineId);
    const movingInfoRef = useRef<{
        hasMouseDown: boolean;
        startMovePoint: {
            x: number;
            y: number;
        } | null;
        lastMovePoint: {
            x: number;
            y: number;
        } | null;
    }>({
        hasMouseDown: false,
        startMovePoint: null,
        lastMovePoint: null,
    });

    // 解决最侧边拖拽线与图层拉伸按钮同时出现的问题
    {
        const removeStyleRef = useRef<(() => void) | null>(null);

        useEffect(() => {
            return () => {
                removeStyleRef.current?.();
                removeStyleRef.current = null;
            };
        }, []);

        useEventListener('mousemove', (e) => {
            if (movingLineIdRef.current) return;
            if (checkIsInResizeArea(e)) {
                removeStyleRef.current?.();
                const removeStyle = hiddenLine();
                removeStyleRef.current = removeStyle;
            } else {
                removeStyleRef.current?.();
                removeStyleRef.current = null;
            }
        });
    }

    const checkIsInResizeArea = (e: Record<'clientX' | 'clientY', number>) => {
        const rcRect = getLayerResizeOperateAreaRect('rc');
        const bcRect = getLayerResizeOperateAreaRect('bc');
        if (
            rcRect &&
            rcRect.left <= e.clientX &&
            rcRect.right >= e.clientX &&
            rcRect.top <= e.clientY &&
            rcRect.bottom >= e.clientY
        )
            return true;
        if (
            bcRect &&
            bcRect.left <= e.clientX &&
            bcRect.right >= e.clientX &&
            bcRect.top <= e.clientY &&
            bcRect.bottom >= e.clientY
        )
            return true;
        return false;
    };

    const onLineMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, movingLineId: LineId) => {
        if (checkIsInResizeArea(e)) return;
        const targetLine = lines.find((line) => isEqual(movingLineId, line.lineId));
        if (!targetLine) return;

        movingInfoRef.current = {
            hasMouseDown: true,
            startMovePoint: { x: e.clientX, y: e.clientY },
            lastMovePoint: { x: e.clientX, y: e.clientY },
        };
        setMovingLineId(movingLineId);
        handleDragLineStart(movingLineId);
    };

    useEventListener('mousemove', ({ clientX, clientY }) => {
        const { hasMouseDown, lastMovePoint } = movingInfoRef.current;
        if (!hasMouseDown) return;
        if (!lastMovePoint) return;
        const deltaX = clientX - lastMovePoint.x;
        const deltaY = clientY - lastMovePoint.y;
        movingInfoRef.current.lastMovePoint = { x: clientX, y: clientY };
        if (!movingLineId) return;
        const movingLine = lines.find((line) => isEqual(line.lineId, movingLineId));
        if (!movingLine) return;
        handleDragLine(movingLine, { deltaX, deltaY });
    });

    useEventListener('mouseup', () => {
        movingInfoRef.current = {
            hasMouseDown: false,
            startMovePoint: null,
            lastMovePoint: null,
        };
        setMovingLineId(null);
        setState({
            lineIsDragging: false,
        });
        handleDragLineEnd();
    });

    return {
        movingLineId,
        onLineMouseDown,
    };
};

type LineId = { direction: 'horizontal' | 'vertical'; index: number };

type Line = {
    lineId: LineId;
    miniLines: MiniLine[];
};

type MiniLine = {
    /** miniLines中, 水平方向上的索引 */
    hIndex: number;
    /** miniLines中, 竖直方向上的索引 */
    vIndex: number;
    /** 相对于tableLayer左上角 */
    top: number;
    /** 相对于tableLayer左上角 */
    left: number;
    /** 线的方向 */
    direction: 'horizontal' | 'vertical';
    /** 线的长度 */
    lineLength: number;
};

const realLineBold = `calc(${LINE_HOVER_BOLD}px / var(--stage-scale, 1))`;

const getMiniLineStyle = (miniLine: MiniLine): React.CSSProperties => {
    return {
        top: miniLine.direction === 'vertical' ? miniLine.top : 0,
        left: miniLine.direction === 'vertical' ? 0 : miniLine.left,
        width: miniLine.direction === 'vertical' ? realLineBold : miniLine.lineLength,
        height: miniLine.direction === 'vertical' ? miniLine.lineLength : realLineBold,
    };
};

const getLineStyle = (line: Line): React.CSSProperties => {
    const miniLine = line.miniLines[0];
    const lineLen = line.miniLines.reduce((acc, l) => acc + l.lineLength, 0);
    return {
        position: 'absolute',
        top: miniLine.direction === 'vertical' ? 0 : miniLine.top,
        left: miniLine.direction === 'vertical' ? miniLine.left : 0,
        width: miniLine.direction === 'vertical' ? realLineBold : lineLen,
        height: miniLine.direction === 'vertical' ? lineLen : realLineBold,
        transform: miniLine.direction === 'vertical' ? 'translateX(-50%)' : 'translateY(-50%)',
    };
};

const LineComponent = memo<{
    line: Line;
    tableRotate?: number;
    onMouseDown?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, line: Line) => void;
}>(({ line, tableRotate = 0, onMouseDown }) => (
    <div
        className="tableController-line"
        data-line-id={`${line.lineId.direction}-${line.lineId.index}`}
        style={getLineStyle(line)}
    >
        {line.miniLines.map((miniLine) => (
            <MiniLineComponent
                key={`${miniLine.direction}-${miniLine.hIndex}-${miniLine.vIndex}`}
                miniLine={miniLine}
                tableRotate={tableRotate}
                onMouseDown={(e) => onMouseDown?.(e, line)}
            />
        ))}
    </div>
));

LineComponent.displayName = 'LineComponent';

const MiniLineComponent: React.FC<{
    miniLine: MiniLine;
    tableRotate?: number;
    onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
}> = ({ miniLine, tableRotate = 0, onMouseDown }) => {
    const cursorClassNames = useResizeCursor(tableRotate);

    return (
        <div
            className={classNames(
                'tableController-miniLine',
                miniLine.direction === 'vertical' ? cursorClassNames.LC : cursorClassNames.TC,
            )}
            data-line-direction={miniLine.direction}
            style={getMiniLineStyle(miniLine)}
            onMouseDown={onMouseDown}
        ></div>
    );
};

const getLines = (
    tableLayerData: ITableLayerProps,
    tableFakeBorder: Record<'top' | 'left' | 'right' | 'bottom', number>,
): Line[] => {
    const {
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
        cells: cellsProp,
    } = tableLayerData;

    const tops = [
        ...verticalSizes.map((_, index) => verticalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        sum(verticalSizes),
    ];
    const lefts = [
        ...horizontalSizes.map((_, index) => horizontalSizes.slice(0, index).reduce((a, b) => a + b, 0)),
        sum(horizontalSizes),
    ];

    const checkIsBorderMiniLine = (miniLine: MiniLine): boolean => {
        let isBorderLine = false;
        if (miniLine.direction === 'vertical') {
            if (miniLine.hIndex === horizontalSizes.length) isBorderLine = true;
            else {
                isBorderLine = cellsProp
                    .map((rowCells) => rowCells[miniLine.hIndex])
                    .some(
                        (cell) =>
                            cell && miniLine.vIndex >= cell.vIndex && miniLine.vIndex + 1 <= cell.vIndex + cell.rowSpan,
                    );
            }
        } else {
            if (miniLine.vIndex === verticalSizes.length) isBorderLine = true;
            else {
                isBorderLine = cellsProp[miniLine.vIndex]?.some(
                    (cell) =>
                        cell && miniLine.hIndex >= cell.hIndex && miniLine.hIndex + 1 <= cell.hIndex + cell.colSpan,
                );
            }
        }
        return isBorderLine;
    };

    const lines: Line[] = [];
    range(1, verticalSizes.length + 1).forEach((index) => {
        lines.push({
            __id: `horizontal-${index}`,
            lineId: {
                direction: 'horizontal',
                index,
            },
            miniLines: [],
        } as any);
    });
    range(1, horizontalSizes.length + 1).forEach((index) => {
        lines.push({
            __id: `vertical-${index}`,
            lineId: {
                direction: 'vertical',
                index,
            },
            miniLines: [],
        } as any);
    });
    for (let hIndex = 0; hIndex <= horizontalSizes.length; hIndex++) {
        const isLastHIndex = hIndex === horizontalSizes.length;
        const left = tableFakeBorder.left + lefts[hIndex];
        for (let vIndex = 0; vIndex <= verticalSizes.length; vIndex++) {
            const isLastVIndex = vIndex === verticalSizes.length;
            const top = tableFakeBorder.top + tops[vIndex];
            if (vIndex !== 0 && !isLastHIndex) {
                const miniLine: MiniLine = {
                    hIndex,
                    vIndex,
                    top,
                    left,
                    direction: 'horizontal',
                    lineLength: horizontalSizes[hIndex],
                };
                if (checkIsBorderMiniLine(miniLine)) {
                    const line__id = `horizontal-${vIndex}`;
                    lines.find((l) => (l as any).__id === line__id)?.miniLines.push(miniLine);
                }
            }
            if (hIndex !== 0 && !isLastVIndex) {
                const miniLine: MiniLine = {
                    hIndex,
                    vIndex,
                    top,
                    left,
                    direction: 'vertical',
                    lineLength: verticalSizes[vIndex],
                };
                if (checkIsBorderMiniLine(miniLine)) {
                    const line__id = `vertical-${hIndex}`;
                    lines.find((l) => (l as any).__id === line__id)?.miniLines.push(miniLine);
                }
            }
        }
    }
    return lines.filter((l) => l.miniLines.length);
};

const useHandleDragLine = (layerId: string) => {
    /**
     * 拖拽这两种线(https://xiuxiu-pro.meitudata.com/posters/9edeccf9f20bb83d55e490c3ba50d787.png)的时候, 如果没处理好, 就会导致这种bug (https://xiuxiu-pro.meitudata.com/posters/cf2a1b0f3ede8a4fc971fface747b3c1.png)
     * 这是解决思路:
     * 1. 应受到影响的几个tr取消高度(自由坍塌)
     * 2. 不应受影响且在这几个tr里的td高度写死, 防止变形
     * 只需要再给`应受影响且在这几个tr里的td`高度设置最小值, 就能达到极限时的布局效果
     * 拖拽过程中, 只需要再给`应受影响且在这几个tr里的td`高度设置值, 就能达到拖拽时的布局效果
     *
     * 注: 表格很大时, 拖拽会卡顿, 所以需要优化, 优化思路:
     * 拖拽时不考虑以上问题, 只需要考虑影响上一行, 拖拽后去自动修复, 只要保证最终布局不错乱就行
     *
     * TODO: 拖拽这两种线(https://xiuxiu-pro.meitudata.com/posters/9edeccf9f20bb83d55e490c3ba50d787.png)还是有bug:
     *  1. content为'111'的单元格的rowMinSize计算得不对 (不知道咋搞啊...)
     *  2. 不能只靠rowMinSize约束极限布局, 还需要靠线到表格顶部的最小值来约束, 不然拖拽'222'的单元格到最小值时, 也会有问题
     */

    const { $t } = useLocaleStore();
    const framework = useFramework();
    const tableLayerOnDragStartRef = useRef<ITableLayer | null>(null);
    const resultTableLayerRef = useRef<ITableLayer | null>(null);
    const intervalTimerRef = useRef<any>();
    const colMinSize = MIN_BASE_CELL_WIDTH;
    const rowMinSizeRef = useRef<number | null>(null);
    const tempTableElRef = useRef<HTMLTableElement | null>(null);
    type AbortRowData = {
        minRowIndex: number;
        maxRowIndex: number;
        effectedCells: Pick<Cell, '__renderId' | 'hIndex' | 'vIndex'>[];
        noEffectedCells: Pick<Cell, '__renderId' | 'hIndex' | 'vIndex'>[];
    };
    const abortRowDataRef = useRef<AbortRowData | null>(null);
    const tableFakeBordersRef = useRef<{
        top: number;
        right: number;
        bottom: number;
        left: number;
    }>({
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    });

    type DragData = { deltaX: number; deltaY: number };

    const checkShouldPerformOptimize = (tableLayerData: Pick<ITableLayerProps, 'cells' | 'baseCells'>) => {
        // 30行以上就得优化了, 不能边拖拽变操作dom, 不然会卡顿, 30行以上直接走兜底修复的逻辑, 拖拽时有点错位不打紧
        const hasMergedCell = tableLayerData.cells.flat().some((c) => c && c.rowSpan > 1);
        const isBigTable = tableLayerData.baseCells.vertical.length > 30;
        return hasMergedCell && isBigTable;
    };

    const resetStates = () => {
        abortRowDataRef.current = null;
        rowMinSizeRef.current = null;
        tableFakeBordersRef.current = {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
        };
        tempTableElRef.current?.remove();
        tempTableElRef.current = null;
        clearInterval(intervalTimerRef.current);
        resultTableLayerRef.current = null;
        tableLayerOnDragStartRef.current = null;
    };

    const handleDragLineStart = (lineId: LineId) => {
        resetStates();

        const tableLayer = framework.getLayer(layerId) as ITableLayer;
        if (!tableLayer) return;

        tableLayerOnDragStartRef.current = tableLayer;
        resultTableLayerRef.current = tableLayer;

        const shouldPerformOptimize = lineId.direction === 'horizontal' && checkShouldPerformOptimize(tableLayer.data);

        const abortRowData = (() => {
            if (shouldPerformOptimize) {
                return null;
            }
            const abortRowData: AbortRowData = {
                minRowIndex: Infinity,
                maxRowIndex: -Infinity,
                effectedCells: [],
                noEffectedCells: [],
            };
            const cells = tableLayer.data.cells.flat().filter(Boolean) as Cell[];
            const effectedCells = cells.filter((c) => c.vIndex < lineId.index && c.vIndex + c.rowSpan >= lineId.index);
            abortRowData.effectedCells = effectedCells;
            effectedCells.forEach((c) => {
                if (c.vIndex < abortRowData.minRowIndex) {
                    abortRowData.minRowIndex = c.vIndex;
                }
                if (c.vIndex + c.rowSpan > abortRowData.maxRowIndex) {
                    abortRowData.maxRowIndex = c.vIndex + c.rowSpan;
                }
            });
            const noEffectedCells = cells.filter(
                (c) =>
                    !effectedCells.includes(c) &&
                    c.vIndex >= abortRowData.minRowIndex &&
                    c.vIndex + c.rowSpan <= abortRowData.maxRowIndex,
            );
            abortRowData.noEffectedCells = noEffectedCells;
            return abortRowData;
        })();
        abortRowDataRef.current = abortRowData;

        if (lineId.direction === 'horizontal') {
            rowMinSizeRef.current = (() => {
                let minSize: number = 0;
                const scale = framework.stage.getScale();
                getUpdatedTableStyle({
                    frameworkIdentity: framework.identity,
                    tableLayerId: tableLayer.id,
                    tableRotate: tableLayer.rotate,
                    action: (el) => {
                        const style = document.createElement('style');
                        el.appendChild(style);
                        const rowElIndex = lineId.index - 1;
                        if (shouldPerformOptimize) {
                            style.innerHTML = `
                                #${el.id} tr:nth-child(${rowElIndex + 1}),
                                #${el.id} tr:nth-child(${rowElIndex + 1}) > td {
                                    height: ${MIN_BASE_CELL_HEIGHT}px !important;
                                }
                            `;
                        } else {
                            let innerHTML = '';
                            const [minTrElIndex, maxTrElIndex] = [
                                abortRowData!.minRowIndex,
                                abortRowData!.maxRowIndex - 1,
                            ];
                            innerHTML += `
                                #${el.id} tr:nth-child(n + ${minTrElIndex + 1}):nth-child(-n + ${maxTrElIndex + 1}) {
                                    height: initial !important;
                                }
                                #${el.id} tr:nth-child(n + ${minTrElIndex + 1}):nth-child(-n + ${
                                    maxTrElIndex + 1
                                }) > td {
                                    height: initial !important;
                                }
                            `;
                            abortRowData!.noEffectedCells
                                .map((c) => getTdElementId({ id: c.__renderId, hIndex: c.hIndex, vIndex: c.vIndex }))
                                .forEach((tdElId) => {
                                    const elHeight =
                                        el.querySelector(`#${tdElId}`)!.getBoundingClientRect().height / scale;
                                    innerHTML += `
                                        #${el.id} td#${tdElId} > div {
                                            min-height: ${elHeight}px !important;
                                        }
                                    `;
                                });
                            abortRowData!.effectedCells
                                .map((c) => getTdElementId({ id: c.__renderId, hIndex: c.hIndex, vIndex: c.vIndex }))
                                .forEach((tdElId) => {
                                    innerHTML += `
                                        #${el.id} td#${tdElId} {
                                            height: initial !important;
                                        }
                                        #${el.id} td#${tdElId} > div {
                                            min-height: ${MIN_BASE_CELL_HEIGHT}px !important;
                                        }
                                    `;
                                });
                            style.innerHTML = innerHTML;
                        }
                        minSize = el.rows[rowElIndex].getBoundingClientRect().height / scale;
                    },
                    compute: noop,
                });
                return Math.max(minSize, MIN_BASE_CELL_HEIGHT);
            })();
        }

        const getTableFakeBorders = () => {
            const firstCell = tableLayer.data.cells[0][0]!;
            // 最右下角的cell
            const lastCell = (() => {
                const cells = tableLayer.data.cells.flat().filter(Boolean) as Cell[];
                const sortedTargetCells = cells.sort((a, b) =>
                    a.hIndex + a.colSpan <= b.hIndex + b.colSpan && a.vIndex + a.rowSpan <= b.vIndex + b.rowSpan
                        ? -1
                        : 1,
                );
                return sortedTargetCells[sortedTargetCells.length - 1];
            })();
            const firstCellStyle = getCellStyle(firstCell, tableLayer.data);
            const lastCellStyle = getCellStyle(lastCell, tableLayer.data);
            const tableFakeBorders = {
                top: (firstCellStyle.border?.top.width ?? 0) / 2,
                right: (lastCellStyle.border?.right.width ?? 0) / 2,
                bottom: (lastCellStyle.border?.bottom.width ?? 0) / 2,
                left: (firstCellStyle.border?.left.width ?? 0) / 2,
            };
            return tableFakeBorders;
        };
        tableFakeBordersRef.current = getTableFakeBorders();

        // 数据越多, render时长越久, 所需延迟就越多
        const delay = tableLayer.data.cells.flat().length * 0.01;

        intervalTimerRef.current = setInterval(() => {
            if (resultTableLayerRef.current) {
                if (!resultTableLayerRef.current.__runtime) {
                    resultTableLayerRef.current.__runtime = {};
                }
                // 拖拽中禁止数据的自动修复, 否则拖拽完后会偏差很大
                resultTableLayerRef.current.__runtime.disableAutoDataFix = true;
                resultTableLayerRef.current = addOrUpdateLayer({
                    frameworkIdentity: framework.identity,
                    layer: resultTableLayerRef.current,
                    resetMaterialIds: false,
                });
            }
        }, delay);
    };

    const handleDragLineEnd = () => {
        if (resultTableLayerRef.current && tableLayerOnDragStartRef.current) {
            if (!resultTableLayerRef.current.__runtime) {
                resultTableLayerRef.current.__runtime = {};
            }
            // 兜底修复: 防止拖拽过程中出现类似问题 https://xiuxiu-pro.meitudata.com/posters/cf2a1b0f3ede8a4fc971fface747b3c1.png
            resultTableLayerRef.current.__runtime.shouldFixLayout = true;
            // 还原状态
            delete resultTableLayerRef.current.__runtime.disableAutoDataFix;
            resultTableLayerRef.current = getFixedTableNumber(resultTableLayerRef.current);
            resultTableLayerRef.current = addOrUpdateLayer({
                frameworkIdentity: framework.identity,
                layer: resultTableLayerRef.current,
                resetMaterialIds: false,
            });
            framework.operate.setActiveLayer(resultTableLayerRef.current.id);
            framework.history.write(cloneDeep(tableLayerOnDragStartRef.current), resultTableLayerRef.current);
        }
        resetStates();
    };

    const handleDragLine = (line: Line, { deltaX, deltaY }: DragData) => {
        const stageScale = framework.stage.getScale();
        const delta = (line.lineId.direction === 'vertical' ? deltaX : deltaY) / stageScale;
        const target = line.lineId.direction === 'vertical' ? 'width' : 'height';

        /** COL_ELEMENT_IS_BLOCK为false的情况下，指定某个cell的width去修复horizontalSizes (只能尽量去修复hSizes, 如果全是合并单元格, 找不到能修复的cell的话, 就不管了) */
        const getCellIdOfFixHSizes = (tableLayer: ITableLayer): string | null => {
            if (target === 'height') return null;
            if (COL_ELEMENT_IS_BLOCK) return null;

            const colIndex = line.lineId.index - 1;
            const targetCells = tableLayer.data.cells
                .flat()
                .filter((c) => c && c.content && c.hIndex === colIndex && c.colSpan === 1) as Cell[];
            if (!targetCells.length) return null;

            // 计算量太大了, 直接不处理
            if (tableLayer.data.baseCells.vertical.length > 5) return null;

            const targetCellId =
                maxBy(targetCells, (c) => getCellStyle(c, tableLayer.data).fontSize)?.__renderId ?? null;

            return targetCellId;
        };

        const getNewTableLayer = (oldTableLayer: ITableLayer): ITableLayer => {
            if (!delta) return oldTableLayer;

            const rowMinSize = rowMinSizeRef.current!;

            // 简单clone
            const newTableLayer: ITableLayer = {
                ...oldTableLayer,
                data: {
                    ...oldTableLayer.data,
                    baseCells: {
                        horizontal: [...oldTableLayer.data.baseCells.horizontal],
                        vertical: [...oldTableLayer.data.baseCells.vertical],
                    },
                },
            };
            const {
                baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
            } = newTableLayer.data;

            if (target === 'height') {
                const rowIndex = line.lineId.index - 1;
                if (verticalSizes[rowIndex] + delta <= rowMinSize) {
                    Message.info($t((v) => v.tableLayer.behavior.limitHeight));
                    return oldTableLayer;
                }

                const shouldPerformOptimize = checkShouldPerformOptimize(newTableLayer.data);
                if (shouldPerformOptimize) {
                    verticalSizes[line.lineId.index - 1] += delta;
                    const tableFakeBorders = tableFakeBordersRef.current;
                    newTableLayer.height = sum(verticalSizes) + tableFakeBorders.top + tableFakeBorders.bottom;
                } else {
                    const newInfo = getUpdatedTableStyle({
                        frameworkIdentity: framework.identity,
                        tableLayerId: newTableLayer.id,
                        tableRotate: newTableLayer.rotate,
                        action: (el) => {
                            if (!tempTableElRef.current) {
                                tempTableElRef.current = el;
                            }

                            const styleId = 'lineDragActionStyle';
                            let style = el.querySelector(`#${styleId}`);
                            if (!style) {
                                style = document.createElement('style');
                                style.id = styleId;
                                el.appendChild(style);
                            }

                            const abortRowData = abortRowDataRef.current;

                            let innerHTML = '';
                            const [minTrElIndex, maxTrElIndex] = [
                                abortRowData!.minRowIndex,
                                abortRowData!.maxRowIndex - 1,
                            ];
                            innerHTML += `
                                #${el.id} tr:nth-child(n + ${minTrElIndex + 1}):nth-child(-n + ${maxTrElIndex + 1}) {
                                    height: initial !important;
                                }
                            `;
                            abortRowData!.noEffectedCells
                                .map((c) => getTdElementId({ id: c.__renderId, hIndex: c.hIndex, vIndex: c.vIndex }))
                                .forEach((tdElId) => {
                                    const elHeight =
                                        el.querySelector(`#${tdElId}`)!.getBoundingClientRect().height / stageScale;
                                    innerHTML += `
                                        #${el.id} td#${tdElId} {
                                            height: ${elHeight}px !important;
                                        }
                                    `;
                                });
                            abortRowData!.effectedCells
                                .map((c) => getTdElementId({ id: c.__renderId, hIndex: c.hIndex, vIndex: c.vIndex }))
                                .forEach((tdElId) => {
                                    const rowNewHeight =
                                        el.querySelector(`#${tdElId}`)!.getBoundingClientRect().height / stageScale +
                                        delta;
                                    innerHTML += `
                                    #${el.id} td#${tdElId} {
                                        height: ${rowNewHeight}px !important;
                                    }
                                `;
                                });
                            style.innerHTML = innerHTML;
                        },
                        compute: (_, { getLayout }) => getLayout(),
                        preserveTempElement: true,
                    })!;
                    newTableLayer.height = newInfo.tableHeight;
                    newTableLayer.data.baseCells.vertical = newInfo.baseCells.vertical;
                }

                return newTableLayer;
            }

            const colIndex = line.lineId.index - 1;

            const cellIdOfFixHSizes = getCellIdOfFixHSizes(oldTableLayer);
            const fixedHSizes = [...newTableLayer.data.baseCells.horizontal];

            if (horizontalSizes[colIndex] + delta <= colMinSize) {
                Message.info($t((v) => v.tableLayer.behavior.limitWidth));
                return oldTableLayer;
            }

            const minDeltaOfWidth = colMinSize - horizontalSizes[colIndex];
            const deltaOfWidth = Math.max(delta, minDeltaOfWidth);

            const newInfo = getUpdatedTableStyle({
                frameworkIdentity: framework.identity,
                tableLayerId: newTableLayer.id,
                tableRotate: newTableLayer.rotate,
                action: (el) => {
                    if (!tempTableElRef.current) {
                        tempTableElRef.current = el;
                    }

                    // 美图秀秀内嵌(qtWebEngine)用max-content, 会导致文字内容无法撑开单元格(即文字被单元格截断), 所以得用initial
                    el.style.width = delta >= 0 ? 'max-content' : 'initial';

                    const colEl = el.querySelectorAll('col')[colIndex];
                    colEl.style.width = horizontalSizes[colIndex] + deltaOfWidth + 'px';

                    if (!COL_ELEMENT_IS_BLOCK && cellIdOfFixHSizes !== null) {
                        const cellOfFixHSizes = newTableLayer.data.cells
                            .flat()
                            .find((c) => c && c.__renderId === cellIdOfFixHSizes);
                        if (!cellOfFixHSizes) {
                            throw new Error('cellOfFixHSizes not found');
                        }
                        const tdElId = getTdElementId({
                            id: cellOfFixHSizes.__renderId,
                            hIndex: cellOfFixHSizes.hIndex,
                            vIndex: cellOfFixHSizes.vIndex,
                        });
                        const tdEl = el.querySelector(`#${tdElId}`);
                        if (!tdEl) {
                            throw new Error('tdEl not found');
                        }
                        const targetColWidth = tdEl.getBoundingClientRect().width / stageScale;
                        fixedHSizes[colIndex] = targetColWidth;
                    }
                },
                compute: (_, { getLayout }) => getLayout(),
                preserveTempElement: true,
            })!;

            if (COL_ELEMENT_IS_BLOCK) {
                newTableLayer.width = newInfo.tableWidth;
                newTableLayer.height = newInfo.tableHeight;
                newTableLayer.data.baseCells = newInfo.baseCells;
            } else {
                // 降级处理
                const tableBorderLeft =
                    getCellStyle(newTableLayer.data.cells[0][0]!, newTableLayer.data).border?.left.width ?? 0;
                const lastSideCell = newTableLayer.data.cells
                    .filter((rowCells) => rowCells.some((c) => c !== null))
                    .slice(-1)[0]
                    .find(Boolean)!;
                const tableBorderRight = getCellStyle(lastSideCell, newTableLayer.data).border?.right.width ?? 0;
                newTableLayer.height = newInfo.tableHeight;
                newTableLayer.data.baseCells.vertical = newInfo.baseCells.vertical;

                if (cellIdOfFixHSizes) {
                    newTableLayer.data.baseCells.horizontal = fixedHSizes;
                } else {
                    newTableLayer.data.baseCells.horizontal[colIndex] += deltaOfWidth;
                }
                newTableLayer.width =
                    sum(newTableLayer.data.baseCells.horizontal) + tableBorderLeft / 2 + tableBorderRight / 2;
            }
            return newTableLayer;
        };

        const oldTableLayer = resultTableLayerRef.current;
        if (!oldTableLayer) return;
        const newLayer = getNewTableLayer(oldTableLayer);
        resultTableLayerRef.current = newLayer;
    };

    return {
        handleDragLineStart,
        handleDragLine,
        handleDragLineEnd,
        resultTableLayerRef,
    };
};

const SideBarHoverArea: React.FC<{
    tableInfo: TableInfo;
    tableLayerData: ITableLayerProps;
    tableFakeBorder: {
        top: number;
        left: number;
        right: number;
        bottom: number;
    };
    direction: 'horizontal' | 'vertical';
    rowOrColIndex: number;
    onSelectedRowOrCol?: (rowOrColIndex: number) => void;
}> = ({ tableInfo, tableLayerData, tableFakeBorder, direction, rowOrColIndex, onSelectedRowOrCol }) => {
    const framework = useFramework();
    const scale = useScale();
    const [addBtnIsShow, setAddBtnIsShow] = useState(false);
    const insertDirectionRef = useRef<InsertDirection | null>(null);

    const add = () => {
        const { addRow, addCol } = getRowOrColActions(framework.identity);
        const tableLayer = framework.getLayer(tableInfo.id) as ITableLayer | undefined;
        if (!tableLayer) return;
        const action = direction === 'horizontal' ? addCol : addRow;
        let newTableLayer = action({
            tableLayer,
            targetIndex: rowOrColIndex,
            insertDirection: insertDirectionRef.current!,
        });
        if (newTableLayer) {
            newTableLayer = addOrUpdateLayer({
                frameworkIdentity: framework.identity,
                layer: newTableLayer,
                resetMaterialIds: false,
            });
            framework.operate.setActiveLayer(newTableLayer.id);
            framework.history.write(cloneDeep(tableLayer), newTableLayer);
        }
    };

    const beforeFakeBorder = direction === 'horizontal' ? tableFakeBorder.left : tableFakeBorder.top;
    const afterFakeBorder = direction === 'horizontal' ? tableFakeBorder.right : tableFakeBorder.bottom;
    const baseSizes =
        direction === 'horizontal' ? tableLayerData.baseCells.horizontal : tableLayerData.baseCells.vertical;
    const isLastIndex = rowOrColIndex === baseSizes.length;

    const getHoverAreaStyle = (
        from: 'pre' | 'current',
    ): Partial<Record<'top' | 'left' | 'width' | 'height', number>> => {
        let size = (from === 'current' ? baseSizes[rowOrColIndex] : baseSizes[rowOrColIndex - 1]) / 2;
        let leftOrTop =
            sum(baseSizes.slice(0, rowOrColIndex)) - (from === 'pre' ? baseSizes[rowOrColIndex - 1] / 2 : 0);
        if (rowOrColIndex === 0) {
            size += beforeFakeBorder;
            leftOrTop += beforeFakeBorder;
        } else if (isLastIndex) {
            size += afterFakeBorder;
        }
        if (direction === 'vertical') {
            return {
                height: size,
                top: leftOrTop,
            };
        }
        return {
            width: size,
            left: leftOrTop,
        };
    };

    const getHoverAreaNode = (from: 'pre' | 'current') => (
        <div
            className={hoverAreaClassName}
            onMouseEnter={() => {
                setAddBtnIsShow(true);
                insertDirectionRef.current = from === 'pre' ? 'bottomOrRight' : 'topOrLeft';
            }}
            onMouseDown={(e) => {
                e.stopPropagation();
                onSelectedRowOrCol?.(from === 'pre' ? rowOrColIndex - 1 : rowOrColIndex);
            }}
            style={getHoverAreaStyle(from)}
        ></div>
    );

    const getAddBtnWrapperStyle = (): React.CSSProperties => {
        let leftOrTop: number;
        if (rowOrColIndex === 0) {
            leftOrTop = 0;
        } else if (isLastIndex) {
            leftOrTop = direction === 'horizontal' ? tableInfo.width : tableInfo.height;
        } else {
            const currentHoverAreaStyle = getHoverAreaStyle('current');
            leftOrTop =
                direction === 'horizontal'
                    ? tableFakeBorder.left + currentHoverAreaStyle.left!
                    : tableFakeBorder.top + currentHoverAreaStyle.top!;
        }
        if (direction === 'vertical') {
            return {
                top: leftOrTop,
                left: -SIDE_BAR_BOLD / scale,
                width: SIDE_BAR_BOLD / scale + tableInfo.width,
                height: 1,
            };
        }
        return {
            top: -SIDE_BAR_BOLD / scale,
            left: leftOrTop,
            width: 1,
            height: SIDE_BAR_BOLD / scale + tableInfo.height,
        };
    };

    // 达到极值了, 不可再添加
    const rowOrColIsLimit = baseSizes.length === (direction === 'horizontal' ? MAX_COL_COUNT : MAX_ROW_COUNT);

    return (
        <div
            onMouseLeave={() => {
                insertDirectionRef.current = null;
                setAddBtnIsShow(false);
            }}
        >
            {rowOrColIndex !== 0 && getHoverAreaNode('pre')}
            {!isLastIndex && getHoverAreaNode('current')}

            {/* 添加行列快捷按钮 */}
            {/* {!rowOrColIsLimit && addBtnIsShow && (
                <div className="addRowOrColBtnWrapper" style={getAddBtnWrapperStyle()}>
                    <div
                        className="addRowOrColBtn"
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            add();
                        }}
                    >
                        <IconFont name="PlusBlack" color="#FFFFFF" />
                    </div>
                    <div className="addRowOrColBtnLine"></div>
                </div>
            )} */}
        </div>
    );
};

const useRowOrColSelectedRect = ({
    tableInfo,
    tableLayerData,
    tableFakeBorder,
}: {
    tableInfo: TableInfo;
    tableLayerData: ITableLayerProps;
    tableFakeBorder: {
        top: number;
        left: number;
        right: number;
        bottom: number;
    };
}) => {
    const framework = useFramework();
    const { setState } = useTableEditState();

    const rowOrColSelectedRectInfo = useTableEditState()((s) => s.rowOrColSelectedRectInfo);
    const { horizontal: horizontalSizes, vertical: verticalSizes } = tableLayerData.baseCells;

    type Rect = Record<'top' | 'left' | 'width' | 'height', number>;
    const selectedRect: Rect | null = (() => {
        if (!rowOrColSelectedRectInfo) return null;

        const { direction, index } = rowOrColSelectedRectInfo;
        const sizes = direction === 'horizontal' ? verticalSizes : horizontalSizes;
        const leftOrTop =
            index === 0
                ? 0
                : sum(sizes.slice(0, index)) +
                  (direction === 'horizontal' ? tableFakeBorder.top : tableFakeBorder.left);
        const beforeFakeBorder = direction === 'horizontal' ? tableFakeBorder.top : tableFakeBorder.left;
        const afterFakeBorder = direction === 'horizontal' ? tableFakeBorder.bottom : tableFakeBorder.right;
        const isLastIndex = index + 1 === sizes.length;
        const size = sizes[index] + (index === 0 ? beforeFakeBorder : isLastIndex ? afterFakeBorder : 0);

        if (direction === 'horizontal') {
            return {
                top: leftOrTop,
                left: 0,
                width: tableInfo.width,
                height: size,
            };
        }
        return {
            top: 0,
            left: leftOrTop,
            width: size,
            height: tableInfo.height,
        };
    })();

    const onSelectedRowOrCol = (info: RowOrColSelectedRectInfo) => {
        const tableLayer = framework.getLayer(tableInfo.id) as ITableLayer;
        if (!tableLayer) return;

        setState({
            activeCell: null,
            cellsIsSelecting: false,
            cellsSelectedInfo: null,
            rowOrColSelectedRectInfo: info,
        });
    };

    const selectedRectNode = !!selectedRect && <div className="rowOrColSelectedRect" style={selectedRect}></div>;

    return {
        selectedRectNode,
        onSelectedRowOrCol,
    };
};
